import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { hideCollectionAddModal } from '../../actions/Collections';
import {
  unsetSelectedFile,
  clearSelectedFiles,
  removeFilesFromFilesList,
  setSelectedAllFiles,
} from '../../actions/Files';

import {
  APIaddFilesToCollection,
  APImoveFilesToCollection,
} from '../../api/Collections';
import {
  APIcreateFilesGroup,
  APIfinishFilesGroup,
} from '../../api/Files';

import CollectionSelectCollectionMessage from './SelectCollectionMessage';
import CollectionsTree from './Tree';
import ProcessingProgress from '../ProcessingProgress';
import ProcessingSpinner from '../ProcessingSpinner';

import { eventDispatch } from '../../helpers/Events';
import { getCurrentFileFilters } from '../../helpers/Files';
import { addFlashMessage } from '../../helpers/FlashMessages';

class CollectionAddModal extends Component {
  constructor(props) {
    super(props);

    this.state = this.getInitialState();

    this.handleClose = this.handleClose.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleAddToCollection = this.handleAddToCollection.bind(this);
    this.handleAddNewCollection = this.handleAddNewCollection.bind(this);
  }

  getInitialState() {
    return {
      collection: null,
      fileGroupUuid: null,
      errors: {},
      saving: false,
    };
  }

  getFiles() {
    const {
      selectedFile,
      selectedFiles,
    } = this.props;

    if (selectedFile) {
      return [selectedFile];
    }

    return selectedFiles;
  }

  getCollectionByUuid(collectionUuid) {
    const { collectionsTree } = this.props;

    if (collectionsTree.tree && collectionsTree.tree[collectionUuid]) {
      return collectionsTree.tree[collectionUuid];
    }

    return null;
  }

  getProgress() {
    const { backendActions } = this.props;
    const { fileGroupUuid } = this.state;

    return backendActions[`progress_${fileGroupUuid}`] || { total: 0, done: 0 };
  }

  setSuccessMessage() {
    const { intl, collectionMoveModal } = this.props;
    const { collection } = this.state;

    let text = '';
    if (collectionMoveModal) {
      text = intl.formatMessage({
        defaultMessage: 'Files have been moved to Collection: {collection}',
      }, { collection: collection.label });
    } else {
      text = intl.formatMessage({
        defaultMessage: 'Files have been added to Collection: {collection}',
      }, { collection: collection.label });
    }

    addFlashMessage({
      type: 'success',
      text,
      button: {
        url: `/collections/${collection.uuid}`,
        text: intl.formatMessage({ defaultMessage: 'Open Collection' }),
      },
    });
  }

  setInitialState() {
    const {
      closeCollectionAddModal,
      clearSelectedFile,
      clearAllSelectedFiles,
    } = this.props;

    this.setState(this.getInitialState());
    closeCollectionAddModal();
    clearSelectedFile();
    clearAllSelectedFiles();
  }

  validateForm() {
    const { intl } = this.props;
    const { collection } = this.state;
    const errors = {};

    if (!collection) {
      errors.collection = intl.formatMessage({ defaultMessage: 'This field is required' });
    }

    this.setState({ errors });

    if (Object.keys(errors).length > 0) {
      return false;
    }

    return true;
  }

  handleChange(value) {
    this.setState({ collection: value });
  }

  handleAddNewCollection(collection) {
    this.setState({
      collection: {
        uuid: collection.uuid,
        value: collection.name,
        label: collection.name,
      },
    });
  }

  finish(targetCollection) {
    const { fileGroupUuid } = this.state;

    eventDispatch('collectionUpdated', targetCollection);
    this.setSuccessMessage();
    this.setInitialState();
    APIfinishFilesGroup(fileGroupUuid, { collection_uuid: targetCollection.uuid });
  }

  handleAddToCollection() {
    if (!this.validateForm()) {
      return;
    }

    const { collection } = this.state;
    const {
      currentCollection,
      removeMovedFilesFromList,
      collectionMoveModal,
      selectedAllFiles,
    } = this.props;

    this.setState({ saving: true });

    const files = selectedAllFiles ? [] : this.getFiles().map(file => (file.uuid));
    const groupCollectionUuid = collectionMoveModal ? currentCollection.uuid : collection.uuid;
    APIcreateFilesGroup({
      type: collectionMoveModal ? 'collection_move_files' : 'collection_add_files',
      data: {
        collection_uuid: groupCollectionUuid,
        target_collection_uuid: collectionMoveModal ? collection.uuid : undefined,
      },
    }).then((response) => {
      this.setState({ fileGroupUuid: response.data.uuid }, () => {
        const { fileGroupUuid } = this.state;
        if (collectionMoveModal) {
          APImoveFilesToCollection(
            collection.uuid,
            {
              files,
              sourceCollectionUuid: currentCollection.uuid,
              fileGroupUuid,
              selectedAllFiles,
              filters: selectedAllFiles ? getCurrentFileFilters() : undefined,
            },
          ).then((res) => {
            removeMovedFilesFromList(this.getFiles());
            this.finish(res.data);
          });
        } else {
          APIaddFilesToCollection(
            collection.uuid,
            {
              files,
              fileGroupUuid,
              selectedAllFiles,
              filters: selectedAllFiles ? getCurrentFileFilters() : undefined,
            },
          ).then((res) => {
            this.finish(res.data);
          });
        }
      });
    });
  }

  handleClose() {
    const { closeCollectionAddModal } = this.props;

    this.setState(this.getInitialState());
    closeCollectionAddModal();
  }

  handleKeyPress(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleAddToCollection();
    }
  }

  render() {
    const {
      collection,
      errors,
      saving,
    } = this.state;
    const {
      intl,
      collectionAddModal,
      collectionMoveModal,
      currentCollection,
      selectedAllFiles,
      filesTotal,
    } = this.props;
    const files = this.getFiles();
    const progress = this.getProgress();
    const totalSelectedFiles = selectedAllFiles ? filesTotal : files.length;

    return (
      <Modal
        isOpen={collectionAddModal}
        toggle={this.handleClose}
      >
        <ModalHeader toggle={this.handleClose} className="modal-header-center">
          {
            collectionMoveModal ?
              intl.formatMessage({
                defaultMessage: 'Move {count} {count, plural, one {file} other {files}} to Collection',
              }, { count: totalSelectedFiles }) :
              intl.formatMessage({
                defaultMessage: 'Add {count} {count, plural, one {file} other {files}} to Collection',
              }, { count: totalSelectedFiles })
          }
        </ModalHeader>
        <ModalBody style={{ overflow: 'visible' }}>
          {collectionMoveModal && (
            <p>
              {intl.formatMessage({
                defaultMessage: `You are moving {count} {count, plural, one {file} other {files}}
                  from your current Collection <strong>{currentCollection}</strong> to the Collection you choose below.
                  Doing this will not remove the {count, plural, one {file} other {files}} from other
                  Collections {count, plural, one {it belongs} other {they belong}} to.`,
              }, {
                count: totalSelectedFiles,
                currentCollection: currentCollection ? currentCollection.name : null,
                strong: (msg) => (
                  msg && <strong>{msg}</strong>
                ),
              })}
            </p>
          )}
          <Form
            className="add-to-collection-form"
            onChange={this.handleChange}
            onKeyPress={this.handleKeyPress}
          >
            <FormGroup>
              <CollectionSelectCollectionMessage />
              <CollectionsTree
                enableMyCollections={false}
                onlyOpenCollections={true}
                addNewDisabled={saving}
                selectedCollections={currentCollection ? [currentCollection.uuid] : []}
                onChange={(selectedUuids) => {
                  if (selectedUuids.length > 0) {
                    const selectedCollection = this.getCollectionByUuid(selectedUuids[0]);
                    if (selectedCollection) {
                      this.setState({ collection: selectedCollection });
                    }
                  } else {
                    this.setState({ collection: null });
                  }
                }}
                smartCollectionsRestrict={false}
                showSmartSection={false}
                bypassMyCollectionsRestriction={true}
              />
              {errors.collection && (
                <FormFeedback style={{ display: 'block' }}>
                  {errors.collection}
                </FormFeedback>
              )}
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={this.handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving || !collection}
              onClick={this.handleAddToCollection}
            >
              <ProcessingSpinner show={saving} />
              {collectionMoveModal ? intl.formatMessage({ defaultMessage: 'Move' }) :
                intl.formatMessage({ defaultMessage: 'Add' })}
              <ProcessingProgress show={saving && !!progress} progress={progress} />
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = state => ({
  selectedFile: state.Files.selectedFile,
  selectedFiles: state.Files.selectedFiles,
  selectedAllFiles: state.Files.selectedAllFiles,
  filesTotal: state.Files.filesTotal,
  collectionAddModal: state.Collections.addModal,
  collectionMoveModal: state.Collections.moveModal,
  currentCollection: state.Collections.collection,
  collectionsTree: state.Collections.collectionsTree,
  addModalOpen: state.Collections.addModal,
  sidebarCollection: state.Collections.sidebarCollection,
  backendActions: state.App.backendActions,
});

const mapDispatchToProps = dispatch => ({
  closeCollectionAddModal: () => {
    dispatch(hideCollectionAddModal());
    dispatch(unsetSelectedFile());
  },
  clearSelectedFile: () => {
    dispatch(unsetSelectedFile());
  },
  clearAllSelectedFiles: () => {
    dispatch(clearSelectedFiles());
    dispatch(setSelectedAllFiles(false));
  },
  removeMovedFilesFromList: (files) => {
    dispatch(removeFilesFromFilesList(files));
  },
});

CollectionAddModal.propTypes = {
  intl: PropTypes.object.isRequired,
  selectedFile: PropTypes.object,
  selectedFiles: PropTypes.array.isRequired,
  selectedAllFiles: PropTypes.bool.isRequired,
  filesTotal: PropTypes.number.isRequired,
  collectionAddModal: PropTypes.bool.isRequired,
  collectionMoveModal: PropTypes.bool.isRequired,
  currentCollection: PropTypes.object,
  collectionsTree: PropTypes.object,
  backendActions: PropTypes.object.isRequired,
  closeCollectionAddModal: PropTypes.func.isRequired,
  clearSelectedFile: PropTypes.func.isRequired,
  clearAllSelectedFiles: PropTypes.func.isRequired,
  removeMovedFilesFromList: PropTypes.func.isRequired,
};

CollectionAddModal.defaultProps = {
  selectedFile: null,
  currentCollection: null,
  collectionsTree: null,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(CollectionAddModal));
